/**
 * Контракты регистрации на клиенте.
 *
 * Порт `AccountsGateway` отделяет функцию от способа доставки: та же логика
 * работает поверх HTTP и поверх заглушки в тестах
 * (docs/ARCHITECTURE.md §3.3).
 *
 * Ни один метод порта не вызывается по ходу набора (ТЗ NFR-S-11): занятости
 * адреса форма не спрашивает, подсказок не показывает, и запрос уходит один —
 * по нажатию кнопки. Проверка занятости «на лету» стоила бы запроса на каждую
 * букву и заодно позволяла бы перебором узнать, кто зарегистрирован.
 */

import type { InstallationId, SyncId, UserId } from '@type2x/shared-contracts';

/** Данные формы регистрации и входа: поля те же, различается намерение. */
export interface RegistrationForm {
  readonly email: string;
  readonly password: string;
}

/** Проблема, понятная пользователю. */
export type RegistrationProblem =
  | 'invalid-email'
  | 'weak-password'
  | 'invalid-credentials'
  | 'progress-taken'
  | 'rate-limited'
  | 'offline'
  | 'server-error';

/**
 * Что стало с локальным прогрессом.
 *
 * Одно значение вместо набора флагов: состояния взаимно исключают друг друга,
 * и сочетание «перенесён и одновременно принадлежит другому» не существует.
 */
export type ProgressOutcome =
  /** Перенесён в аккаунт этим действием. */
  | 'moved'
  /** Уже принадлежал этому аккаунту: повторный вход переносом не является. */
  | 'already'
  /** Принадлежит другому аккаунту и остался на месте. */
  | 'taken'
  /** Переносить было нечего. */
  | 'none';

/**
 * Спокойное уведомление о конце trial (ТЗ FR-SUB-03).
 *
 * Считает его сервер: часы браузера для сроков не годятся. `none` — говорить
 * не о чем, `ended` — trial уже кончился, и предупреждать поздно.
 */
export type TrialNotice = 'none' | 'three-days' | 'one-day' | 'ended';

/** Адрес подтверждён: аккаунт существует, trial идёт. */
export interface VerifiedAccount {
  readonly kind: 'verified';
  readonly userId: UserId;
  /** Что стало с локальным прогрессом. */
  readonly progress: ProgressOutcome;
  /** Дней trial осталось. */
  readonly trialDaysLeft: number;
  /** Пора ли предупредить о конце trial (ТЗ FR-SUB-03). */
  readonly trialNotice: TrialNotice;
}

/**
 * Вход в существующий аккаунт.
 *
 * Неподтверждённый адрес входу не мешает: аккаунт существует, и человек
 * вправе им пользоваться. Premium он не открывает — это решает сервер.
 */
export interface SignedIn {
  readonly kind: 'signed-in';
  readonly userId: UserId;
  /** Под каким адресом вошли: «вы вошли в аккаунт» не отвечает, в какой. */
  readonly email: string;
  readonly emailVerified: boolean;
  readonly progress: ProgressOutcome;
  readonly trialDaysLeft: number;
  readonly trialNotice: TrialNotice;
}

/**
 * Письмо отправлено, подтверждения ещё нет.
 *
 * Тот же исход и у занятого адреса: сервер намеренно не различает их в
 * ответе (ТЗ SEC-11), поэтому не различает и клиент. Ждать письма — верное
 * действие в обоих случаях.
 */
export interface AwaitingVerification {
  readonly kind: 'awaiting-verification';
  /**
   * Секрет этого браузера. По нему он потом узнает, что адрес подтвердили —
   * возможно, с другого устройства.
   */
  readonly claimToken: string | null;
}

/** Итог регистрации: либо подтверждённый аккаунт, либо ожидание письма. */
export type RegistrationOutcome = VerifiedAccount | AwaitingVerification;

/** Ответ сервера на регистрацию. */
export interface RegisterReply {
  readonly status: string;
  /**
   * Токен подтверждения. Приходит, только пока сервер не доставляет писем;
   * с настоящим почтовым адаптером здесь всегда `null`.
   */
  readonly verificationToken: string | null;
  /**
   * Секрет этого браузера: по нему он спросит, не подтвердили ли адрес с
   * другого устройства. Приходит и для занятого адреса — иначе его отсутствие
   * раскрывало бы существование аккаунта.
   */
  readonly claimToken: string | null;
}

/** Ответ сервера на подтверждение адреса. */
export interface SessionReply {
  readonly userId: UserId;
  /** Адрес, под которым открыта сессия: свой собственный, по куке. */
  readonly email: string;
  readonly emailVerified: boolean;
}

/**
 * Что переносить в аккаунт после подтверждения адреса.
 *
 * `null` вместо этих данных означает, что переносить нечего: подтверждение
 * пришло туда, где локального прогресса нет. Занимать установку в этом случае
 * незачем — она пуста, а идентификатор одноразовый.
 */
export interface ProgressTransfer {
  readonly installationId: InstallationId;
  readonly syncId: SyncId;
}

/** Ответ сервера на перенос установки. */
export interface ClaimReply {
  readonly claimed: boolean;
  readonly alreadyClaimed: boolean;
}

/** Состояние trial, полученное от сервера. */
export interface TrialReply {
  readonly active: boolean;
  readonly daysLeft: number;
  readonly notice: TrialNotice;
}

/** Порт доставки запросов регистрации. */
export interface AccountsGateway {
  register(form: RegistrationForm, installationId: InstallationId): Promise<RegisterReply>;
  /** Открывает сессию по адресу и паролю. */
  login(form: RegistrationForm): Promise<SessionReply>;
  /**
   * Спрашивает сервер, кто вошёл. `null` — никто.
   *
   * Без этого вопроса приложение знает о состоянии аккаунта ровно до
   * перезагрузки вкладки: подтверждён ли адрес и идёт ли trial, оно помнило
   * только как итог последнего действия.
   */
  currentSession(): Promise<SessionReply | null>;
  /**
   * Спрашивает, не подтвердили ли регистрацию этого браузера.
   *
   * `null` — ещё не подтвердили либо секрет ничей. Подтверждение открывает
   * сессию тому, кто спросил.
   */
  registrationStatus(claimToken: string): Promise<SessionReply | null>;
  verifyEmail(token: string): Promise<SessionReply>;
  /** Просит сервер отправить письмо подтверждения ещё раз. */
  resendVerification(email: string): Promise<void>;
  /** Просит сервер прислать ссылку восстановления пароля. */
  requestPasswordReset(email: string): Promise<void>;
  /** Меняет пароль по токену из письма. */
  confirmPasswordReset(token: string, password: string): Promise<void>;
  /**
   * Завершает сессию.
   *
   * Маршрут был написан с самого начала и не вызывался ни разу: войти можно
   * было, выйти — нет. Оставалось удалить аккаунт или чистить cookie руками
   * (задание §15 — «регистрации, входа, выхода и восстановления»).
   */
  logout(): Promise<void>;
  /** Отдаёт всё, что сервер хранит о пользователе. */
  exportAccount(): Promise<unknown>;
  /** Удаляет аккаунт. Пароль подтверждает намерение. */
  deleteAccount(password: string): Promise<void>;
  claim(installationId: InstallationId, syncId: SyncId): Promise<ClaimReply>;
  trialStatus(): Promise<TrialReply>;
}

/** Ошибка регистрации с понятной пользователю причиной. */
export class RegistrationError extends Error {
  constructor(readonly problem: RegistrationProblem) {
    super(problem);
    this.name = 'RegistrationError';
  }
}
